import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { cloneDeep, set, uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { customStyles, shortenString } from '../../../Components/helper';
import { commonPath } from '../../DevlopmentOrder/Bom/BomProvider/filedPaths';
import { fetchUserSuppliers } from '../../Supplier/store/Supplier.action';
import BomImage from './BomImage';
import { toasterMsg } from '../../../Components/Toaster/Toaster';
import { fetchBomComponentSubTypeFromBomList } from './store/BomLibrary.action';
import ImageUploadModal from '../../../Components/ModalPopup/ImageUploadModal';
import DeletePopup from '../../../Components/ModalPopup/DeletePopup';
import CustomTooltip from '../../../Components/Tooltip/CustomTooltip';
import { getImagesByLocation, IMAGE_PICKER_EXTENSIONS } from '../../AssetLibrary/Assets/store/asset.service';
import { getS3FileAsBlob } from '../../../services/s3Services';

function BomSubType({
  dataValue,
  dataIndex,
  bomDocumentState,
  setBomDocumentState,
  setCoumnHeader,
  columnHeader,
  id,
  setBomDeletedData,
  compositionErrors,
  setCompositionErrors,
  BOMId,
  isSuggestionMode,
  devOrderId,
}) {
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const { t } = useTranslation('common');
  const [referenceSupp, setReferenceSupp] = useState([]);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [selectedBomId, setBomId] = useState(null);
  const reducer = useSelector((reducerState) => reducerState);
  const dispatch = useDispatch();
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const text = 'BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.COMPOSITION';
  const { UserDetails, SystemMastersDetails, SuppliersDetails, BomLibraryDetails, SupplierSuggestion } = reducer;
  const { tier2Suppliers, tier1Suppliers } = SuppliersDetails;
  const { ReferenceType, Composition, UnitOfMeasure } = BomLibraryDetails;
  const activeReferenceType = useMemo(() => ReferenceType?.filter((option) => option?.isActive) ?? [], [ReferenceType]);
  const { finishedOptions, units } = SupplierSuggestion;
  const { userInfo } = UserDetails;
  const { finalList } = SystemMastersDetails;
  const userRole = userInfo.role;
  const loggedInUserId = userInfo.userId;
  const bomId = BOMId;

  const handleChange = (value, path) => {
    const clonedCardList = cloneDeep(bomDocumentState?.cardList);
    if (
      path.includes('BOMSupplier') ||
      path.includes('bomComponentType') ||
      path.includes('bomComponentSubType') ||
      path.includes('sizeUnitOfMeasure') ||
      path.includes('weightUnitOfMeasure') ||
      path.includes('weightUnitOfMeasure') ||
      path.includes('quantityUnitOfMeasure') ||
      path.includes('finished')
    ) {
      set(clonedCardList, path, value?._id);
    } else {
      set(clonedCardList, path, value);
    }

    if (path.includes('bomComponentType')) {
      const bomComponentSubTypePath = commonPath(dataIndex, 'bomComponentSubType').COMMON_PATH;
      set(clonedCardList, bomComponentSubTypePath, null);
    }
    setBomDocumentState((previousState) => ({ ...previousState, cardList: clonedCardList }));
  };

  const handleChangeComposition = (event, index, field) => {
    const fieldName = event?.currentTarget?.name ? event.currentTarget.name : 'composition';
    let value;
    if (event?.target?.value?.length > 0) {
      value = event.target.value;
    } else if (field === 'composition') {
      value = event?._id;
    } else {
      value = null;
    }
    const column = [...dataValue.composition];
    const clonedRowsIndex = cloneDeep(column[index]);
    column[index] = { ...clonedRowsIndex, [fieldName]: value || null };
    const path = commonPath(dataIndex, 'composition').COMMON_PATH;
    const copyState = cloneDeep(bomDocumentState?.cardList);
    set(copyState, path, column);
    setBomDocumentState((previousState) => ({ ...previousState, cardList: copyState }));

    const errors = [...compositionErrors];

    const totalPercentage = column.reduce((sum, comp) => {
      return sum + (parseFloat(comp.percentage) || 0);
    }, 0);

    errors[dataIndex] = {};

    if (totalPercentage !== 100) {
      errors[dataIndex].percentage = `Error: ${totalPercentage}`;
    } else {
      errors[dataIndex].percentage = ''; // Clear any previous errors if valid
    }

    column.forEach((comp) => {
      if (comp.percentage && !comp.composition) {
        errors[dataIndex].composition = t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.COMPOSITION_PERCENTAGE_REQUIRED');
      } else {
        errors[dataIndex].composition = '';
      }
    });

    setCompositionErrors(errors);
  };

  /**
   * Write the chosen image onto this sub-BOM row, merging the given fields over the current row.
   * @param {Object} imageFields Image values and asset flags to set on the row.
   */
  const updateRowImage = (imageFields) => {
    setBomDocumentState((previousState) => {
      const copyState = cloneDeep(previousState?.cardList);
      Object.entries(imageFields).forEach(([field, value]) => set(copyState, commonPath(dataIndex, field).COMMON_PATH, value));
      return { ...previousState, cardList: copyState };
    });
  };

  /**
   * Called by ImageUploadModal on save.
   *
   * Inside a development order the product BOM endpoint understands the Asset Library: a library pick
   * is sent as its raw S3 key (flagged `isUploadAsset`, with `previewImage` holding the presigned URL
   * for the thumbnail), and a device upload carries the "Add to Asset Library" tick.
   *
   * The library-level and supplier-suggestion saves only persist `data:image` payloads, so there a
   * library pick is proxy-fetched by its key and encoded to base64 like an upload. The modal crops in
   * place, so the saved image fills both `image` and `fullImage`.
   */
  const handleImageSave = async ({ file, assetUrl, assetKey, assetId, addToAssetLibrary }) => {
    if (assetKey && devOrderId) {
      updateRowImage({
        image: assetKey,
        fullImage: assetKey,
        previewImage: assetUrl,
        isUploadAsset: true,
        assetId: assetId || null,
        isAddToAssetLibrary: false,
      });
      return;
    }
    try {
      const blob = file || (assetKey ? await getS3FileAsBlob({ objectKey: assetKey }) : null);
      if (!blob) {
        toasterMsg(t('SPEC_TEMP_LIBRARY.IMAGE_REQUIRED'), 'danger');
        return;
      }
      const base64Image = await readBlobAsDataUrl(blob);
      updateRowImage({
        image: base64Image,
        fullImage: base64Image,
        previewImage: '',
        isUploadAsset: false,
        assetId: null,
        isAddToAssetLibrary: Boolean(devOrderId && file && addToAssetLibrary),
      });
    } catch {
      toasterMsg(t('DESIGN_DETAILS.FAILED_TO_LOAD_IMAGE'), 'danger');
    }
  };

  /** Fetch asset-library images for the picker, scoped to the development order when there is one. */
  const fetchSubBomImages = useCallback(
    (search, page, limit, allowedImageExtensions) => getImagesByLocation('Bill of Materials', devOrderId, search, allowedImageExtensions, page, limit),
    [devOrderId],
  );

  const confirmDeleteImage = () => {
    updateRowImage({ image: '', fullImage: '', previewImage: '', isUploadAsset: false, assetId: null, isAddToAssetLibrary: false });
    setShowDeleteModal(false);
  };

  const closeModal = () => {
    setShowDeleteModal(false);
  };

  const handleDeleteCard = (index) => {
    setBomId(index);
    setShowConfirmationPopup(!showConfirmationPopup);
  };

  const toogleAddEditModal = (e) => {
    e.preventDefault();
    const copyState = cloneDeep(bomDocumentState?.cardList);
    if (copyState?.length >= 30) {
      toasterMsg(t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.MAX_ROWS'), 'danger');
      return;
    }

    copyState.push({
      error: false,
      isSave: false,
      isDefault: false,
      index: 1,
      composition: columnHeader
        ?.filter((data) => data === text)
        .map(() => ({
          percentage: null,
          composition: null,
        })),
    });
    setBomDocumentState((prevState) => ({ ...prevState, cardList: copyState, editBomDataId: [], selectedIds: [], isMultiEditing: false }));
  };

  useEffect(() => {
    if (dataValue?.composition?.length > 1 && id) {
      setCoumnHeader((prevState) => {
        const currentLength = prevState.length;
        const neededColumns = dataValue.composition.length - currentLength; // Adjust by subtracting 1
        if (neededColumns > 0) {
          const newHeaders = Array(neededColumns).fill(text);
          return [...prevState, ...newHeaders];
        }
        return prevState;
      });
    }
  }, [dataValue?.composition, id]);

  const deleteById = async () => {
    let updateData = cloneDeep(bomDocumentState?.cardList);

    const updatedData = { ...updateData[selectedBomId], isDeleted: true };
    setBomDeletedData((prevData) => [...prevData, updatedData]);

    updateData = updateData.filter((data, index) => index !== selectedBomId);

    if (!updateData.length) {
      updateData.push({
        isSave: false,
        error: false,
        isDefault: true,
        composition: columnHeader
          ?.filter((data) => data === text)
          .map(() => ({
            percentage: null,
            composition: null,
          })),
      });
    }
    setBomId(null);
    setBomDocumentState((oldState) => ({ ...oldState, cardList: updateData }));
    setShowConfirmationPopup(false);
  };

  useEffect(() => {
    let rs = [];
    if (tier2Suppliers?.length > 0 || tier1Suppliers?.length > 0) {
      const rsArray = uniqBy([...tier1Suppliers, ...tier2Suppliers], 'name');
      const uniqueRs = [];
      const uniqueRsArray = [];
      rsArray.forEach((val) => {
        if (uniqueRs.indexOf(val._id) === -1) {
          uniqueRs.push(val._id);
          uniqueRsArray.push(val);
        }
      });
      rs.push(...uniqueRsArray);
      rs.forEach((ele) => {
        ele.label = ele.name;
        ele.value = ele._id;
      });
      const checkuserRole = async () => {
        if (userRole === 'User') {
          const userSuppliers = await dispatch(fetchUserSuppliers({ userId: loggedInUserId }));
          if (Array.isArray(userSuppliers)) {
            rs = rs.filter((val) => {
              if (userSuppliers.includes(val._id)) return val;
            });
          }
        }
      };
      checkuserRole();
      setReferenceSupp(rs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier2Suppliers, tier1Suppliers, dispatch]);

  const handleImageUpload = () => {
    setShowImageUpload(true);
  };

  const handleImageDelete = () => {
    setShowDeleteModal(true);
  };

  const componentTypeId = useMemo(() => {
    if (dataValue?.bomComponentType?._id) {
      return dataValue.bomComponentType._id;
    }
    if (dataValue?.bomComponentType) {
      return dataValue.bomComponentType;
    }
    return null;
  }, [dataValue?.bomComponentType?._id, dataValue?.bomComponentType]);

  const prevValuesRef = useRef({ bomId: null, componentTypeId: null });

  useEffect(() => {
    async function fetchAPI() {
      if (bomId && componentTypeId) {
        const prevBOMId = prevValuesRef.current.bomId;
        const prevComponentTypeId = prevValuesRef.current.componentTypeId;

        if (prevBOMId === bomId && prevComponentTypeId === componentTypeId) {
          return;
        }

        prevValuesRef.current = { bomId, componentTypeId };

        const res = await dispatch(fetchBomComponentSubTypeFromBomList({ bomTypeId: bomId, componentTypeId }));
        if (res?.payload) {
          setBomDocumentState((previousState) => {
            const copyState = cloneDeep(previousState.cardList);
            const optionPath = commonPath(dataIndex, 'bomComponentSubTypeBomList').COMMON_PATH;
            set(copyState, optionPath, res.payload);
            return { ...previousState, cardList: copyState };
          });
        }
      }
    }
    fetchAPI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bomId, componentTypeId, dispatch, dataIndex]);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <tbody>
        <tr key={`row_${dataValue?._id || dataIndex}`}>
          <td>{`1.${dataIndex + 1}`}</td>
          <td>
            <BomImage dataValue={dataValue} handleImageUpload={handleImageUpload} handleImageDelete={handleImageDelete} t={t} />
          </td>
          <td className="last-fix-col">
            <div className="common-input-control mb-0">
              <input
                type="text"
                name={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.REFERENCE_CODE')}
                placeholder={
                  screenWidth <= 1968
                    ? shortenString(t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.ENTER_COMPONENT_CODE'), 15)
                    : t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.ENTER_COMPONENT_CODE')
                }
                value={dataValue?.referenceCode || ''}
                onChange={(e) => handleChange(e?.target.value, commonPath(dataIndex, 'referenceCode').COMMON_PATH)}
                className="form-control f-14 sub-bom-input"
              />
            </div>
            {dataValue?.error && !dataValue?.referenceCode && (
              <span className="error-message">{t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.COMPONENT_CODE_REQUIRED')}</span>
            )}
          </td>
          <td>
            <div className="common-input-control mb-0">
              <input
                type="text"
                name={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.BOM_DESCRIPTION')}
                placeholder={
                  screenWidth <= 1968
                    ? shortenString(t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.ENTER_COMPONENT_DESCRIPTION'), 15)
                    : t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.ENTER_COMPONENT_DESCRIPTION')
                }
                value={dataValue?.bomDescription || ''}
                onChange={(e) => handleChange(e?.target.value, commonPath(dataIndex, 'bomDescription').COMMON_PATH)}
                className="form-control f-14 sub-bom-input"
              />
            </div>
          </td>

          <td className="add-form">
            <div className="common-react-select mb-0">
              <Select
                menuPlacement="top"
                menuPosition="fixed"
                name={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.BOM_SUPPLIER')}
                options={referenceSupp}
                getOptionLabel={(option) => option?.name}
                getOptionValue={(option) => option?._id}
                value={referenceSupp?.find((option) => option?._id === dataValue?.BOMSupplier?._id || option?._id === dataValue?.BOMSupplier) || null}
                styles={customStyles}
                placeholder={
                  screenWidth <= 1968
                    ? shortenString(t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.SELECT_COMPONENT_SUPLIER'), 6)
                    : t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.SELECT_COMPONENT_SUPLIER')
                }
                onChange={(selected) => handleChange(selected, commonPath(dataIndex, 'BOMSupplier').COMMON_PATH)}
                isClearable
                className="common-select sub-bom-select"
                classNamePrefix="common-select"
              />
            </div>
          </td>
          <td className="add-form">
            <div className="common-react-select mb-0">
              <Select
                menuPlacement="top"
                className="common-select sub-bom-select"
                classNamePrefix="common-select"
                menuPosition="fixed"
                name={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.BOM_COMPONENT_TYPE')}
                options={activeReferenceType}
                getOptionLabel={(option) => option?.name}
                getOptionValue={(option) => option?._id}
                placeholder={
                  screenWidth <= 1968
                    ? shortenString(t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.SELECT_COMPONENT_TYPE'), 6)
                    : t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.SELECT_COMPONENT_TYPE')
                }
                value={ReferenceType?.find((option) => option?._id === dataValue?.bomComponentType?._id || option?._id === dataValue?.bomComponentType) || null}
                styles={customStyles}
                onChange={(selected) => handleChange(selected, commonPath(dataIndex, 'bomComponentType').COMMON_PATH)}
                isClearable
              />
            </div>
            {dataValue?.error && !dataValue?.bomComponentType && (
              <span className="error-message">{t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.COMPONENT_TYPE_REQUIRED')}</span>
            )}
          </td>
          <td className="add-form">
            <div className="common-react-select mb-0">
              <Select
                menuPlacement="top"
                menuPosition="fixed"
                className="common-select sub-bom-select"
                classNamePrefix="common-select"
                name={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.BOM_COMPONENT_SUB_TYPE')}
                options={dataValue?.bomComponentSubTypeBomList?.filter((data) => data?.isActive)}
                getOptionLabel={(option) => option?.name}
                getOptionValue={(option) => option?._id}
                placeholder={
                  screenWidth <= 1968
                    ? shortenString(t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.SELECT_COMPONENT_SUB_TYPE'), 6)
                    : t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.SELECT_COMPONENT_SUB_TYPE')
                }
                value={
                  dataValue?.bomComponentSubTypeBomList?.find(
                    (option) => option?._id === dataValue?.bomComponentSubType?._id || option?._id === dataValue?.bomComponentSubType,
                  ) || null
                }
                styles={customStyles}
                onChange={(selected) => handleChange(selected, commonPath(dataIndex, 'bomComponentSubType').COMMON_PATH)}
                isClearable
              />
            </div>
          </td>
          <td className="sub-bom-size">
            <div className="d-flex align-items-center">
              <div className="common-input-control mb-0">
                <input
                  type="text"
                  name={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.size')}
                  placeholder={shortenString(t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.ENTER_SIZE'), 10)}
                  value={dataValue?.size || ''}
                  onChange={(e) => handleChange(e?.target.value, commonPath(dataIndex, 'size').COMMON_PATH)}
                  className="form-control f-14 sub-bom-input"
                />
              </div>
              <div className="inline-unit add-form">
                <div className="common-react-select mb-0">
                  <Select
                    menuPlacement="top"
                    menuPosition="fixed"
                    name={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.SIZE_UNIT_MEASURE')}
                    options={Array.isArray(UnitOfMeasure) && UnitOfMeasure.length > 0 ? UnitOfMeasure : units}
                    getOptionLabel={(option) => option?.name}
                    getOptionValue={(option) => option?._id}
                    value={
                      UnitOfMeasure?.find((option) => option?._id === dataValue?.sizeUnitOfMeasure?._id || option?._id === dataValue?.sizeUnitOfMeasure) ||
                      units?.find((option) => option?._id === dataValue?.sizeUnitOfMeasure?._id || dataValue?.sizeUnitOfMeasure) ||
                      null
                    }
                    styles={customStyles}
                    onChange={(selected) => handleChange(selected, commonPath(dataIndex, 'sizeUnitOfMeasure').COMMON_PATH)}
                    isClearable
                    className="common-select sub-bom-select"
                    classNamePrefix="common-select"
                  />
                </div>
              </div>
            </div>
          </td>

          <td className="sub-bom-size">
            <div className="d-flex align-items-center">
              <div className="common-input-control mb-0">
                <input
                  type="text"
                  name={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.weight')}
                  placeholder={
                    screenWidth <= 1968
                      ? shortenString(t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.ENTER_WEIGHT'), 10)
                      : t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.ENTER_WEIGHT')
                  }
                  value={dataValue?.weight || ''}
                  onChange={(e) => handleChange(e?.target.value, commonPath(dataIndex, 'weight').COMMON_PATH)}
                  className="form-control f-14 sub-bom-input"
                />
              </div>
              <div className="inline-unit add-form">
                <div className="common-react-select mb-0">
                  <Select
                    menuPlacement="top"
                    menuPosition="fixed"
                    name={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.WEIGHT_UNIT_MEASURE')}
                    options={Array.isArray(UnitOfMeasure) && UnitOfMeasure.length > 0 ? UnitOfMeasure : units}
                    getOptionLabel={(option) => option?.name}
                    getOptionValue={(option) => option?._id}
                    value={
                      UnitOfMeasure?.find((option) => option?._id === dataValue?.weightUnitOfMeasure?._id || option?._id === dataValue?.weightUnitOfMeasure) ||
                      units?.find((option) => option?._id === dataValue?.weightUnitOfMeasure?._id || dataValue?.weightUnitOfMeasure) ||
                      null
                    }
                    styles={customStyles}
                    onChange={(selected) => handleChange(selected, commonPath(dataIndex, 'weightUnitOfMeasure').COMMON_PATH)}
                    isClearable
                    className="common-select sub-bom-select"
                    classNamePrefix="common-select"
                  />
                </div>
              </div>
            </div>
          </td>

          <td className="sub-bom-size">
            <div className="d-flex align-items-center">
              <div className="common-input-control mb-0">
                <input
                  type="text"
                  name={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.quantity')}
                  placeholder={
                    screenWidth <= 1968
                      ? shortenString(t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.ENTER_QUANTITY'), 10)
                      : t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.ENTER_QUANTITY')
                  }
                  value={dataValue?.quantity || ''}
                  onChange={(e) => handleChange(e?.target.value, commonPath(dataIndex, 'quantity').COMMON_PATH)}
                  className="form-control f-14 sub-bom-input"
                />
              </div>
              <div className="inline-unit add-form">
                <div className="common-react-select mb-0">
                  <Select
                    menuPlacement="top"
                    menuPosition="fixed"
                    name={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.QUANTITY_UNIT_MEASURE')}
                    options={Array.isArray(UnitOfMeasure) && UnitOfMeasure.length > 0 ? UnitOfMeasure : units}
                    getOptionLabel={(option) => option?.name}
                    getOptionValue={(option) => option?._id}
                    value={
                      UnitOfMeasure?.find(
                        (option) => option?._id === dataValue?.quantityUnitOfMeasure?._id || option?._id === dataValue?.quantityUnitOfMeasure,
                      ) ||
                      units?.find((option) => option?._id === dataValue?.quantityUnitOfMeasure?._id || option?._id === dataValue?.quantityUnitOfMeasure) ||
                      null
                    }
                    styles={customStyles}
                    onChange={(selected) => handleChange(selected, commonPath(dataIndex, 'quantityUnitOfMeasure').COMMON_PATH)}
                    isClearable
                    className="common-select sub-bom-select"
                    classNamePrefix="common-select"
                  />
                </div>
              </div>
            </div>
          </td>
          <td className="add-form">
            <div className="common-react-select mb-0">
              <Select
                menuPlacement="top"
                menuPosition="fixed"
                name={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.FINISHED')}
                options={Array.isArray(finalList) && finalList.length > 0 ? finalList : finishedOptions}
                className="common-select sub-bom-select"
                classNamePrefix="common-select"
                getOptionLabel={(option) => option?.name}
                getOptionValue={(option) => option?._id}
                value={
                  finalList?.find((option) => option?._id === dataValue?.finished) ||
                  finishedOptions?.find((option) => option?._id === dataValue?.finished?._id || option?._id === dataValue?.finished) ||
                  null
                }
                styles={customStyles}
                onChange={(selected) => handleChange(selected, commonPath(dataIndex, 'finished').COMMON_PATH)}
                isClearable
                placeholder={shortenString(t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.SELECT_FINISH'), 7)}
              />
            </div>
          </td>

          {dataValue?.composition?.map((data, index) => (
            <td key={index}>
              <div className="d-flex align-items-center">
                <div className="common-input-control mb-0">
                  <div className="d-flex mr-2">
                    <input
                      type="text"
                      name={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.percentage')}
                      value={data?.percentage || ''}
                      onChange={(event) => {
                        const inputValue = event.target.value;
                        const isValidDecimal = /^\d*\.?\d*$/.test(inputValue);
                        if (isValidDecimal) {
                          handleChangeComposition(event, index, 'percentage');
                        }
                      }}
                      className="form-control position-relative f-14 sub-bom-input"
                    />
                    <span className=" new-percentage">%</span>
                  </div>
                  {index === dataValue?.composition.length - 1 && compositionErrors[dataIndex]?.percentage && (
                    <span className="error-message">{t('BOM_TYPE_GROUP_VIEW.VALIDATION_ERROR.TOTAL_PERCENTAGE_VALIDATION')}</span>
                  )}
                </div>
                <div className="flex-fill percentage-select common-react-select mb-0">
                  <Select
                    className="common-select"
                    classNamePrefix="common-select"
                    menuPlacement="top"
                    menuPosition="fixed"
                    name={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.composition')}
                    options={(Array.isArray(Composition) && Composition.length > 0 ? Composition : null) || []}
                    getOptionLabel={(option) => option?.name}
                    getOptionValue={(option) => option?._id}
                    value={
                      (Array.isArray(Composition) && Composition.length > 0
                        ? Composition?.find((option) => option?._id === data.composition?._id || option?._id === data.composition)
                        : null) || null
                    }
                    styles={customStyles}
                    onChange={(event) => handleChangeComposition(event, index, 'composition')}
                    isClearable
                  />
                  {data?.percentage && !data.composition && (
                    <span className="error-message">{t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.COMPOSITION_PERCENTAGE_REQUIRED')}</span>
                  )}
                </div>
              </div>
            </td>
          ))}

          <td className="px-1 increase-z-index">
            <div className="action-btns new-listing-bom d-flex align-items-center test-action-btns">
              {dataIndex === bomDocumentState?.cardList?.length - 1 && (
                <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.ADD')}>
                  <button aria-label="Add" className="btn font-initial line-normal d-inline-flex p-0" type="button" onClick={toogleAddEditModal}>
                    <i className="icon-Add f-20" />
                  </button>
                </CustomTooltip>
              )}
              <CustomTooltip placement="top" tooltipText={t('SPEC_TEMP_LIBRARY.DELETE')}>
                <button
                  className="btn ml-8  font-initial line-normal d-inline-flex p-0"
                  type="button"
                  onClick={() => {
                    handleDeleteCard(dataIndex);
                  }}
                  aria-label={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.DELETE')}
                >
                  <i className="icon-Delete f-20" />
                </button>
              </CustomTooltip>
            </div>
          </td>
        </tr>
      </tbody>
      {showConfirmationPopup && (
        <DeletePopup
          onOpen={showConfirmationPopup}
          onConfirm={deleteById}
          loading={false}
          title={t('SPEC_TEMP_LIBRARY.DELETE_SUBBOM')}
          message={t('SPEC_TEMP_LIBRARY.DELETE_SUBBOM_MESSAGE')}
          toggle={() => setShowConfirmationPopup(!showConfirmationPopup)}
        />
      )}
      {showDeleteModal && (
        <DeletePopup
          onOpen={showDeleteModal}
          onConfirm={() => confirmDeleteImage()}
          title={t('SPEC_TEMP_LIBRARY.REMOVE_IMAGE')}
          message={t('SPEC_TEMP_LIBRARY.REMOVE_MESSAGE')}
          toggle={() => closeModal()}
        />
      )}
      <ImageUploadModal
        show={showImageUpload}
        fetchImages={fetchSubBomImages}
        allowedImageExtensions={IMAGE_PICKER_EXTENSIONS}
        showAddToAssetLibrary={Boolean(devOrderId)}
        existingImage={dataValue?.previewImage || dataValue?.image || null}
        onClose={() => setShowImageUpload(false)}
        onSave={handleImageSave}
      />
    </>
  );
}

export default BomSubType;

/**
 * Read a Blob or File into a base64 `data:` URL.
 * @param {Blob} blob - Image bytes to encode.
 * @returns {Promise<string>} The encoded data URL.
 */
function readBlobAsDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
